import React, { useState } from 'react';
import { useThoughtSpotAuth } from '../hooks/useThoughtSpotAuth';
import AppEmbed from './AppEmbed';
import LiveboardEmbed from './LiveboardEmbed';
import BEM from 'common/bem';

const classes = BEM.with('ClinicalWorkflows');

// ThoughtSpot group that controls what permissions embedded users are provisioned with.
// Kept here so product requirements around access level live in the toolkit, not the SDK.
const THOUGHTSPOT_GROUP = 'Application Control';

// ThoughtSpot liveboard IDs for each embedded view
const LIVEBOARD_IDS: Record<string, string> = {
  'nurse-call': '1d5df012-2905-410d-b283-228fcdbbdda3',
  consults: '3b94194a-b437-41c0-a642-7020b4c6db41',
};

type InsightsView = 'my-liveboards' | 'nurse-call' | 'consults';

const INSIGHTS_VIEWS: { id: InsightsView; label: string }[] = [
  { id: 'my-liveboards', label: 'My Liveboards' },
  { id: 'nurse-call', label: 'Nurse Call' },
  { id: 'consults', label: 'Consults' },
];

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: Record<string, any> | null;
  currentOrganizationId: string;
};

export default function InsightsTab({ client, currentOrganizationId }: Props) {
  const [activeInsightsView, setActiveInsightsView] = useState<InsightsView>('my-liveboards');
  const authState = useThoughtSpotAuth(client, currentOrganizationId, THOUGHTSPOT_GROUP);

  const activeLiveboardId = LIVEBOARD_IDS[activeInsightsView];

  const renderContent = () => {
    if (authState.status === 'loading' || authState.status === 'idle') {
      return <div className={classes('insights-loading')}>Loading analytics…</div>;
    }

    if (authState.status === 'error') {
      return (
        <div className={classes('insights-error')}>
          Unable to load analytics. Please try again later.
        </div>
      );
    }

    if (activeInsightsView === 'my-liveboards') {
      return <AppEmbed host={authState.host} token={authState.token} orgId={authState.orgId} />;
    }

    return (
      <LiveboardEmbed
        liveboardId={activeLiveboardId}
        host={authState.host}
        token={authState.token}
        orgId={authState.orgId}
      />
    );
  };

  return (
    <div className={classes('insights-split')} data-test-id="clinical-workflows-insights-content">
      <div className={classes('insights-nav')}>
        {INSIGHTS_VIEWS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={classes('insights-nav-item', { selected: activeInsightsView === id })}
            onClick={() => setActiveInsightsView(id)}
            data-test-id={`clinical-workflows-insights-${id}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className={classes('insights-content')}>{renderContent()}</div>
    </div>
  );
}
